import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { label } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TONES = {
  green: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  blue: "bg-blue-500/15 text-blue-500 border-blue-500/30",
  amber: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  red: "bg-red-500/15 text-red-500 border-red-500/30",
  cyan: "bg-cyan-500/15 text-cyan-500 border-cyan-500/30",
  slate: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  rose: "bg-rose-600/20 text-rose-400 border-rose-500",
};
const MAP = {
  green: ["online", "active", "available", "successful", "completed", "resolved", "executed", "approved", "pass", "paid", "open_state", "balanced", "whitelist", "synced"],
  blue: ["closed", "exit_pending", "under_review", "staff", "tenant", "info", "superseded"],
  amber: ["payment_pending", "pending", "pending_approval", "maintenance", "reserved", "warning", "gray", "partial", "open", "overdue", "duplicate", "occupied"],
  red: ["offline", "fault", "failed", "exception", "unpaid", "blocked", "escalated", "critical", "blacklist", "refunded", "voided", "rejected", "repeat_violator", "closed_with_discrepancy", "out_of_service"],
  cyan: ["vip", "opening", "closing", "shift", "weekly", "preventive"],
  slate: ["cancelled", "disabled", "n/a", "regular"],
};
const toneOf = (s) => Object.keys(MAP).find((k) => MAP[k].includes(s)) || "slate";

export const StatusBadge = ({ status, testid, tone }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${TONES[tone || toneOf(status)]}`}>
    {label(status || "—")}
  </span>
);

export const Dot = ({ ok }) => (
  <span className="relative inline-flex h-2.5 w-2.5">
    {ok && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />}
    <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${ok ? "bg-emerald-500" : "bg-red-500"}`} />
  </span>
);

export const PageHeader = ({ title, subtitle, children, testid }) => (
  <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between" data-testid={testid}>
    <div>
      <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
    </div>
    <div className="flex flex-wrap items-center gap-2">{children}</div>
  </div>
);

export const Kpi = ({ label: l, value, sub, icon: Icon, tone = "text-foreground", testid, alert }) => (
  <div data-testid={testid} className={`kpi group rounded-lg border bg-card p-4 transition-transform duration-200 hover:-translate-y-0.5 ${alert ? "border-red-500/50" : "border-border/60"}`}>
    <div className="flex items-center justify-between">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{l}</span>
      {Icon && <Icon className={`h-4 w-4 ${alert ? "text-red-500" : "text-muted-foreground"}`} />}
    </div>
    <div className={`mt-2 font-mono text-2xl font-bold tracking-tight ${tone}`}>{value}</div>
    {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

export const Panel = ({ title, action, children, className = "", testid }) => (
  <section data-testid={testid} className={`rounded-lg border border-border/60 bg-card ${className}`}>
    {title && (
      <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
        <h3 className="font-display text-sm font-bold tracking-tight">{title}</h3>
        {action}
      </div>
    )}
    <div className="p-4">{children}</div>
  </section>
);

export function DataTable({ columns, rows, testid, onRowClick, empty = "No records", loading }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border/60 bg-card" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className="whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={columns.length} className="p-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></td>
            </tr>
          )}
          {!loading && !rows?.length && (
            <tr>
              <td colSpan={columns.length} className="p-8 text-center text-muted-foreground">{empty}</td>
            </tr>
          )}
          {!loading &&
            rows?.map((r, i) => (
              <tr
                key={r.id || i}
                data-testid={`${testid}-row-${i}`}
                onClick={() => onRowClick?.(r)}
                className={`border-t border-border/40 transition-colors hover:bg-muted/30 ${onRowClick ? "cursor-pointer" : ""}`}
              >
                {columns.map((c) => (
                  <td key={c.key} className="whitespace-nowrap px-3 py-2.5 align-middle">
                    {c.render ? c.render(r) : r[c.key] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

export function Sel({ value, onChange, options, placeholder = "Select", testid, allLabel, className = "w-full sm:w-44" }) {
  const v = value === "" || value === undefined || value === null ? (allLabel ? "__all" : undefined) : value;
  return (
    <Select value={v} onValueChange={(x) => onChange(x === "__all" ? "" : x)}>
      <SelectTrigger data-testid={testid} className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {allLabel && <SelectItem value="__all">{allLabel}</SelectItem>}
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} data-testid={`${testid}-opt-${o.value}`}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export const opts = (arr) => arr.map((x) => ({ value: x, label: label(x) }));

export function BranchSel({ value, onChange, testid = "branch-filter", all = true, className }) {
  const { branches, user } = useAuth();
  return (
    <Sel
      value={value}
      onChange={onChange}
      testid={testid}
      className={className}
      allLabel={all && user?.all_branches ? "All branches" : undefined}
      placeholder="Branch"
      options={branches.map((b) => ({ value: b.id, label: `${b.code} — ${b.name}` }))}
    />
  );
}

export const Field = ({ label: l, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{l}</Label>
    {children}
  </div>
);

export const Spinner = () => (
  <div className="flex h-40 items-center justify-center">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);
